import { useLocation } from '@remix-run/react';
import { useState } from 'react';
import type { CapiProductPage, Raffle } from 'types/capi/pdp';

import { Button } from '~/components/Button';
import Countdown from '~/components/Countdown';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import { formatRaffleDate } from '~/utils/dates';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

type Props = {
  buttonPosition: number;
  raffleListing: Raffle;
};

const RaffleListing = ({ raffleListing, buttonPosition }: Props) => {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const product = usePageData<CapiProductPage>();
  const { clientBrowser, clientIp } = useRootData();
  const { pathname, search } = useLocation();
  const [hasRaffleEnded, setHasRaffleEnded] = useState(false);

  const { url, clickRef } = getTrackedUrlAndClickRef(raffleListing, {
    buttonPosition,
    user
  });

  const onButtonClick = () => {
    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    return wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product: product,
      url
    });
  };

  return (
    <div className="bg-white">
      <div className="flex cursor-pointer flex-row justify-between gap-3 p-4">
        <div className="flex grow items-center gap-1">
          <div className="h-9 w-16">
            <img
              src={raffleListing.affiliate.logo}
              alt={raffleListing.id}
              className="rounded bg-white object-cover text-black"
            />
          </div>
          <div className="text-nowrap text-base">
            {hasRaffleEnded ? (
              <p>Closed</p>
            ) : (
              <p className="text-forest">Open until</p>
            )}
            <span className="text-sm text-grey-hide">
              {formatRaffleDate(raffleListing.close)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <Countdown
            toUnixTimestamp={raffleListing.close}
            onEnd={() => setHasRaffleEnded(true)}
          />
        </div>
      </div>
      <div className="px-4 pb-6">
        <a
          data-cy="raffle-button"
          onClick={onButtonClick}
          href={url}
          target="_blank"
          rel="sponsored nofollow noopener"
        >
          <Button variant="forest">Enter Raffle</Button>
        </a>
      </div>
    </div>
  );
};

export default RaffleListing;
